import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Search, Download, RefreshCw, Filter, X,
  User, Users, Shield, Eye,
  Package, Loader2, Edit3, AlertTriangle,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 10;

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const downloadCSV = (data, filename) => {
  if (!data?.length) { toast.error('No data to download'); return; }
  const headers = Object.keys(data[0]);
  const rows = data.map(row =>
    headers.map(h => `"${(row[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ── role config ───────────────────────────────────────────────────────────────
const ROLES = [
  { key: 'user',    label: 'Buyers / Users', icon: User,    activeClass: 'bg-blue-50 text-blue-700 border-blue-300' },
  { key: 'seller',  label: 'Sellers',         icon: Package, activeClass: 'bg-green-50 text-green-700 border-green-300' },
  { key: 'agent',   label: 'Agents',          icon: Users,   activeClass: 'bg-purple-50 text-purple-700 border-purple-300' },
  { key: 'partner', label: 'Partners',        icon: Shield,  activeClass: 'bg-amber-50 text-amber-700 border-amber-300' },
  { key: 'edit_logs', label: 'Admin Edit Logs', icon: Edit3, activeClass: 'bg-red-50 text-red-700 border-red-300' },
];

// ── sub-components ────────────────────────────────────────────────────────────
const Sec = ({ title }) => (
  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mt-5 mb-2">{title}</p>
);

const KV = ({ label, value, mono = false, color = '' }) => (
  <div className="flex justify-between items-start gap-2 py-1.5 border-b border-gray-50 text-xs">
    <span className="text-gray-400 flex-shrink-0">{label}</span>
    <span className={`text-right font-medium text-gray-800 break-all ${mono ? 'font-mono' : ''} ${color}`}>{value || '—'}</span>
  </div>
);

const MiniStat = ({ label, value, color = 'bg-gray-50 text-gray-700' }) => (
  <div className={`rounded-xl p-2.5 text-center ${color}`}>
    <p className="text-sm font-bold">{value ?? '—'}</p>
    <p className="text-[10px] mt-0.5 leading-tight">{label}</p>
  </div>
);

// ── ADMIN EDIT LOGS PANEL ─────────────────────────────────────────────────────
const AdminEditLogsPanel = ({ authH }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const LIMIT = 10;

  useEffect(() => { fetchLogs(); }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/superadmin/admin-edit-logs?skip=${(page - 1) * LIMIT}&limit=${LIMIT}`,
        authH
      );
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {
      // silently fail — endpoint may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const filteredLogs = search.trim()
    ? logs.filter(l =>
        l.admin_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.vehicle_id?.includes(search) ||
        l.verification_id?.includes(search)
      )
    : logs;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-red-500" />
            Admin Verification Form Edit Logs
            <Badge className="bg-red-100 text-red-700 text-xs ml-1">{total} total</Badge>
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Every time an admin edits a field in a partner-submitted verification form, it is logged here.
          </p>
        </div>
        <button
          onClick={() => { setPage(1); fetchLogs(); }}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition flex-shrink-0"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by admin name, vehicle ID, or verification ID..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Logs list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-red-400" />
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
          <Edit3 className="h-12 w-12 mx-auto mb-3 text-gray-200" />
          <p className="font-medium text-gray-500">No admin edit logs recorded yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Logs appear here when an admin modifies partner-verified data during inspection review
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLogs.map((log, i) => (
            <div key={log.id || i} className="bg-white border border-gray-200 rounded-2xl p-4 hover:border-red-200 transition">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Admin name + badges */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {log.admin_name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <p className="text-sm font-bold text-gray-900">{log.admin_name || 'Admin'}</p>
                    <Badge className="bg-orange-100 text-orange-700 text-xs">
                      {log.edit_count} field{log.edit_count !== 1 ? 's' : ''} edited
                    </Badge>
                    {log.edit_count > 3 && (
                      <Badge className="bg-red-100 text-red-700 text-xs flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> High edit count
                      </Badge>
                    )}
                  </div>

                  {/* IDs */}
                  <div className="space-y-0.5 mb-2">
                    <p className="text-xs text-gray-500">
                      Vehicle:{' '}
                      <span className="font-mono text-gray-700">
                        {log.vehicle_id ? `${log.vehicle_id.slice(0, 12)}...` : '—'}
                      </span>
                    </p>
                    {log.verification_id && (
                      <p className="text-xs font-mono text-blue-600">{log.verification_id}</p>
                    )}
                    <p className="text-xs text-gray-400">
                      {log.edited_at ? new Date(log.edited_at).toLocaleString('en-IN') : '—'}
                    </p>
                  </div>

                  {/* Fields that were edited */}
                  {log.edits && (
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(log.edits)
                        .filter(([k]) => ![
                          'editCount', 'reviewed_by', 'reviewed_at',
                          'admin_verified', 'partner_selfie_match',
                          'owner_verification', 'admin_notes',
                        ].includes(k))
                        .map(([field, value]) => (
                          <span
                            key={field}
                            className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full"
                          >
                            {field.replace(/_/g, ' ')}: {String(value).slice(0, 25)}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Summary note */}
      <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-xs text-red-700">
        <p className="font-semibold mb-1">ℹ️ About these logs</p>
        <p>
          These logs are auto-created when an admin clicks "Finish Vehicle Admin Verification" after editing any
          field in the 3-column inspection review. Each log records: which admin made the change, which vehicle
          and verification form was affected, how many fields were edited, and the corrected values.
        </p>
      </div>
    </div>
  );
};

// ── DETAIL DRAWER ─────────────────────────────────────────────────────────────
const DetailDrawer = ({ person, roleKey, onClose, authH }) => {
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState({});

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (roleKey === 'user' || roleKey === 'seller') {
          const [vRes, profRes] = await Promise.allSettled([
            axios.get(`${API}/admin/vehicles?limit=1000`, authH),
            axios.get(`${API}/users/${person.id}/profile`),
          ]);

          const allVehicles = vRes.status === 'fulfilled' ? (vRes.value.data.vehicles || []) : [];
          const vehicles = allVehicles.filter(v => v.seller_id === person.id);
          const profile = profRes.status === 'fulfilled' ? profRes.value.data.profile : null;

          const vehiclePayments = vehicles
            .filter(v => v.payment_id)
            .map(v => ({
              payment_id: v.payment_id,
              vehicle: `${v.brand_name || ''} ${v.model_name || ''}`.trim(),
              registration_no: v.registration_no,
              vehicle_display_id: v.vehicle_display_id,
              city: v.city,
              status: v.payment_status || '—',
              coupon_code: v.coupon_code || null,
              featured: v.featured || false,
              verification_requested: v.verification_requested || false,
              expires_at: v.expires_at,
            }));

          let verifications = [];
          try {
            const vsRes = await axios.get(`${API}/superadmin/verification-assignments`, authH);
            verifications = (vsRes.data.assignments || []).filter(
              a => a.seller_id === person.id || a.seller_name === person.name
            );
          } catch {}

          if (!cancelled) setD({
            vehicles,
            vehiclePayments,
            verifications,
            ratings: profile?.ratings || [],
            average_rating: profile?.average_rating || 0,
            total_ratings: profile?.total_ratings || 0,
            total_vehicles: vehicles.length,
            approved: vehicles.filter(v => v.status === 'approved').length,
            pending: vehicles.filter(v => v.status === 'pending').length,
            sold: vehicles.filter(v => v.status === 'sold').length,
            rejected: vehicles.filter(v => v.status === 'rejected').length,
            total_views: vehicles.reduce((s, v) => s + (v.views_count || 0), 0),
            verified_count: vehicles.filter(v => v.verified_by_x7autoz).length,
            featured_count: vehicles.filter(v => v.featured).length,
          });

        } else if (roleKey === 'agent') {
          const [vRes, agentRes] = await Promise.allSettled([
            axios.get(`${API}/admin/vehicles?limit=1000`, authH),
            axios.get(`${API}/superadmin/agents/${person.id}`, authH),
          ]);

          const allVehicles = vRes.status === 'fulfilled' ? (vRes.value.data.vehicles || []) : [];
          const vehicles = allVehicles.filter(v => v.seller_id === person.id);

          const vehiclePayments = vehicles
            .filter(v => v.payment_id)
            .map(v => ({
              payment_id: v.payment_id,
              vehicle: `${v.brand_name || ''} ${v.model_name || ''}`.trim(),
              registration_no: v.registration_no,
              vehicle_display_id: v.vehicle_display_id,
              city: v.city,
              status: v.payment_status || '—',
              coupon_code: v.coupon_code || null,
              featured: v.featured || false,
              verification_requested: v.verification_requested || false,
            }));

          if (!cancelled) setD({
            vehicles,
            vehiclePayments,
            total_vehicles: vehicles.length,
            approved: vehicles.filter(v => v.status === 'approved').length,
            pending: vehicles.filter(v => v.status === 'pending').length,
            sold: vehicles.filter(v => v.status === 'sold').length,
            total_views: vehicles.reduce((s, v) => s + (v.views_count || 0), 0),
            verified_count: vehicles.filter(v => v.verified_by_x7autoz).length,
          });

        } else if (roleKey === 'partner') {
          const [aRes, avRes, asRes, walletRes] = await Promise.allSettled([
            axios.get(`${API}/superadmin/partners/${person.id}/analytics`, authH),
            axios.get(`${API}/superadmin/partner-availability`, authH),
            axios.get(`${API}/superadmin/verification-assignments`, authH),
            axios.get(`${API}/superadmin/commission-wallet`, authH),
          ]);

          const analytics = aRes.status === 'fulfilled' ? aRes.value.data : {};
          const avail = (avRes.status === 'fulfilled' ? avRes.value.data.availability || [] : [])
            .filter(a => a.partner_id === person.id);
          const assignments = (asRes.status === 'fulfilled' ? asRes.value.data.assignments || [] : [])
            .filter(a =>
              a.assigned_partner_id === person.id ||
              a.assigned_partner_name === person.name
            );
          const walletItems = (walletRes.status === 'fulfilled'
            ? walletRes.value.data.line_items || []
            : []
          ).filter(item => item.partner_id === person.id);

          const pendingPayout = walletItems
            .filter(i => i.payout_status === 'pending')
            .reduce((s, i) => s + (i.partner_amount || 0), 0);
          const paidOut = walletItems
            .filter(i => i.payout_status === 'paid')
            .reduce((s, i) => s + (i.partner_amount || 0), 0);

          if (!cancelled) setD({
            analytics,
            avail,
            assignments,
            walletItems,
            pendingPayout,
            paidOut,
            all_time_earnings: analytics.all_time?.total_earnings || 0,
            last_30_days: analytics.last_30_days?.earnings || 0,
            total_verified: analytics.all_time?.vehicles_verified || 0,
            weekly_hours: analytics.weekly_availability_hours || 0,
            meets_minimum: analytics.meets_minimum || false,
          });
        }
      } catch (e) {
        console.error('Drawer load error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [person.id, roleKey]);

  const uid = person.user_uid || person.agent_uid || person.partner_uid || '';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-[500px] h-full overflow-y-auto shadow-2xl flex flex-col">

        {/* Sticky header */}
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-900 truncate">{person.name}</p>
            {uid && <p className="text-[10px] font-mono text-gray-400 mt-0.5">{uid}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 ml-3 flex-shrink-0">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* ── USER ──────────────────────────────────────────────── */}
              {(roleKey === 'user') && (
                <>
                  <Sec title="Identity" />
                  <KV label="User UID" value={person.user_uid} mono color="text-blue-700" />
                  <KV label="Email" value={person.email} />
                  <KV label="Phone" value={person.phone} />
                  <KV label="City" value={person.city} />
                  <KV label="Role" value={person.role} />
                  <KV label="Joined Date" value={fmtDate(person.created_at)} />
                  <KV label="Joined Time" value={fmtTime(person.created_at)} />

                  <Sec title="Interests & Preferences" />
                  {(() => {
                    const i = person.interests || {};
                    return (
                      <>
                        <KV label="Vehicle Type" value={i.vehicle_type} />
                        <KV label="Fuel Type" value={i.fuel_type} />
                        <KV label="Transmission" value={i.transmission} />
                        <KV label="Ownership" value={i.ownership ? `${i.ownership} Owner` : null} />
                        <KV label="Model Year Range" value={i.model_year_range} />
                        <KV label="RTO Codes" value={i.rto_codes?.join(', ')} />
                      </>
                    );
                  })()}

                  <Sec title="Activity" />
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <MiniStat label="Vehicles Listed" value={d.total_vehicles || 0} color="bg-blue-50 text-blue-700" />
                    <MiniStat label="Total Views" value={d.total_views || 0} color="bg-green-50 text-green-700" />
                    <MiniStat label="Verified Vehicles" value={d.verified_count || 0} color="bg-indigo-50 text-indigo-700" />
                  </div>

                  {d.vehicles?.length > 0 && (
                    <>
                      <Sec title="Vehicles Listed" />
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {d.vehicles.map((v, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold">{v.brand_name} {v.model_name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                v.status === 'approved' ? 'bg-green-100 text-green-700'
                                : v.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                                : v.status === 'sold' ? 'bg-blue-100 text-blue-700'
                                : 'bg-red-100 text-red-700'
                              }`}>{v.status}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500">
                              <span>Reg: <strong className="font-mono">{v.registration_no}</strong></span>
                              {v.vehicle_display_id && <span>ID: <strong className="font-mono text-blue-600">{v.vehicle_display_id}</strong></span>}
                              <span>Price: <strong>{v.price}</strong></span>
                              <span><Eye className="h-3 w-3 inline" /> {v.views_count || 0}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── SELLER ────────────────────────────────────────────── */}
              {(roleKey === 'seller') && (
                <>
                  <Sec title="Identity" />
                  <KV label="User UID" value={person.user_uid} mono color="text-green-700" />
                  <KV label="Email" value={person.email} />
                  <KV label="Phone" value={person.phone} />
                  <KV label="City" value={person.city} />
                  <KV label="Joined Date" value={fmtDate(person.created_at)} />
                  <KV label="Joined Time" value={fmtTime(person.created_at)} />
                  {d.average_rating > 0 && (
                    <KV label="Avg Rating" value={`${d.average_rating} ⭐ (${d.total_ratings} reviews)`} />
                  )}

                  <Sec title="Vehicle Stats" />
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <MiniStat label="Total" value={d.total_vehicles} color="bg-gray-50 text-gray-700" />
                    <MiniStat label="Approved" value={d.approved} color="bg-green-50 text-green-700" />
                    <MiniStat label="Pending" value={d.pending} color="bg-yellow-50 text-yellow-700" />
                    <MiniStat label="Sold" value={d.sold} color="bg-blue-50 text-blue-700" />
                    <MiniStat label="Verified" value={d.verified_count} color="bg-indigo-50 text-indigo-700" />
                    <MiniStat label="Total Views" value={d.total_views} color="bg-purple-50 text-purple-700" />
                  </div>

                  {d.vehicles?.length > 0 && (
                    <>
                      <Sec title="Listings" />
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {d.vehicles.map((v, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold">{v.brand_name} {v.model_name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                v.status === 'approved' ? 'bg-green-100 text-green-700'
                                : v.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                                : v.status === 'sold' ? 'bg-blue-100 text-blue-700'
                                : 'bg-red-100 text-red-700'
                              }`}>{v.status}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500">
                              <span>Reg: <strong className="font-mono">{v.registration_no}</strong></span>
                              {v.vehicle_display_id && <span>ID: <strong className="font-mono text-blue-600">{v.vehicle_display_id}</strong></span>}
                              <span>Price: <strong>{v.price}</strong></span>
                              <span><Eye className="h-3 w-3 inline" /> {v.views_count || 0}</span>
                              <span>{v.city}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {v.verification_requested && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">Verification opted</span>}
                              {v.verified_by_x7autoz && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px]">✓ Verified</span>}
                              {v.featured && <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-[10px]">★ Featured</span>}
                              {v.expires_at && <span className="bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded text-[10px]">Expires: {fmtDate(v.expires_at)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.vehiclePayments?.length > 0 && (
                    <>
                      <Sec title="Payment History (via vehicle records)" />
                      <p className="text-[10px] text-gray-400 mb-2">Payment data extracted from vehicle records</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {d.vehiclePayments.map((p, i) => (
                          <div key={i} className="flex justify-between items-start text-xs py-2 border-b border-gray-100">
                            <div className="min-w-0 flex-1">
                              <p className="font-mono font-semibold text-gray-800">{p.payment_id}</p>
                              <p className="text-gray-400 mt-0.5 truncate">{p.vehicle} · {p.registration_no}</p>
                              {p.vehicle_display_id && <p className="font-mono text-[10px] text-blue-600">{p.vehicle_display_id}</p>}
                              <div className="flex flex-wrap gap-1 mt-0.5">
                                {p.coupon_code && <span className="bg-orange-100 text-orange-700 px-1 py-0.5 rounded text-[10px]">Coupon: {p.coupon_code}</span>}
                                {p.featured && <span className="bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded text-[10px]">Featured</span>}
                                {p.verification_requested && <span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[10px]">Verification</span>}
                              </div>
                            </div>
                            <div className="text-right ml-2 flex-shrink-0">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>{p.status}</span>
                              <p className="text-gray-400 text-[10px] mt-0.5">{p.city}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.verifications?.length > 0 && (
                    <>
                      <Sec title="Verification Assignments" />
                      <div className="space-y-2">
                        {d.verifications.map((v, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between mb-1">
                              <span className="font-mono text-blue-600">{v.verification_id || '—'}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                v.status === 'assigned' ? 'bg-blue-100 text-blue-700'
                                : v.status === 'completed' ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                              }`}>{v.status}</span>
                            </div>
                            <p className="text-gray-500">Partner: <strong>{v.assigned_partner_name || '—'}</strong></p>
                            {v.availability_slots?.[0] && (
                              <p className="text-gray-400 mt-0.5">
                                {v.availability_slots[0].date} · {v.availability_slots[0].from_time}–{v.availability_slots[0].to_time}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.ratings?.length > 0 && (
                    <>
                      <Sec title="Reviews Received" />
                      <div className="space-y-2">
                        {d.ratings.map((r, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between">
                              <span className="font-medium">{r.rater_name}</span>
                              <span className="text-yellow-600">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                            </div>
                            {r.review && <p className="text-gray-500 mt-1 italic">"{r.review}"</p>}
                            <p className="text-gray-400 mt-1">{fmtDate(r.created_at)}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── AGENT ─────────────────────────────────────────────── */}
              {(roleKey === 'agent') && (
                <>
                  <Sec title="Identity" />
                  <KV label="Agent UID" value={person.agent_uid} mono color="text-purple-700" />
                  <KV label="Status" value={person.agent_approved ? '✅ Approved' : '⏳ Pending'} />
                  <KV label="Email" value={person.email} />
                  <KV label="Phone" value={person.phone} />
                  <KV label="City" value={person.city} />
                  <KV label="Business Name" value={person.business_name} />
                  <KV label="GST Number" value={person.gst_number} mono />
                  <KV label="Joined Date" value={fmtDate(person.created_at)} />
                  <KV label="Joined Time" value={fmtTime(person.created_at)} />

                  <Sec title="Vehicle Inventory" />
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <MiniStat label="Total" value={d.total_vehicles} color="bg-gray-50 text-gray-700" />
                    <MiniStat label="Approved" value={d.approved} color="bg-green-50 text-green-700" />
                    <MiniStat label="Pending" value={d.pending} color="bg-yellow-50 text-yellow-700" />
                    <MiniStat label="Sold" value={d.sold} color="bg-blue-50 text-blue-700" />
                    <MiniStat label="Verified" value={d.verified_count} color="bg-indigo-50 text-indigo-700" />
                    <MiniStat label="Total Views" value={d.total_views} color="bg-purple-50 text-purple-700" />
                  </div>

                  {d.vehicles?.length > 0 && (
                    <>
                      <Sec title="Listings" />
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {d.vehicles.map((v, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-semibold">{v.brand_name} {v.model_name}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                v.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              }`}>{v.status}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-500">
                              {v.vehicle_display_id && <span>ID: <strong className="font-mono text-blue-600">{v.vehicle_display_id}</strong></span>}
                              <span>Price: <strong>{v.price}</strong></span>
                              <span><Eye className="h-3 w-3 inline" /> {v.views_count || 0}</span>
                              {v.verified_by_x7autoz && <span className="text-green-600 font-semibold">✓ Verified</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.vehiclePayments?.length > 0 && (
                    <>
                      <Sec title="Payment History" />
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {d.vehiclePayments.map((p, i) => (
                          <div key={i} className="flex justify-between text-xs py-2 border-b border-gray-100">
                            <div className="min-w-0 flex-1">
                              <p className="font-mono font-semibold">{p.payment_id}</p>
                              <p className="text-gray-400 truncate">{p.vehicle} · {p.registration_no}</p>
                              <div className="flex gap-1 mt-0.5">
                                {p.coupon_code && <span className="bg-orange-100 text-orange-700 px-1 py-0.5 rounded text-[10px]">Coupon: {p.coupon_code}</span>}
                                {p.featured && <span className="bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded text-[10px]">Featured</span>}
                                {p.verification_requested && <span className="bg-blue-100 text-blue-700 px-1 py-0.5 rounded text-[10px]">Verification</span>}
                              </div>
                            </div>
                            <span className="text-gray-400 text-[10px] ml-2 flex-shrink-0">{p.city}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── PARTNER ───────────────────────────────────────────── */}
              {(roleKey === 'partner') && (
                <>
                  <Sec title="Identity" />
                  <KV label="Partner UID" value={person.partner_uid} mono color="text-amber-700" />
                  <KV label="Status" value={person.active ? '✅ Active' : '❌ Inactive'} />
                  <KV label="Email" value={person.email} />
                  <KV label="Phone" value={person.phone} />
                  <KV label="City" value={person.city} />
                  <KV label="Payout Cycle" value={`${person.payout_cycle_days || 30} days`} />
                  <KV label="Joined Date" value={fmtDate(person.created_at)} />
                  <KV label="Joined Time" value={fmtTime(person.created_at)} />
                  <KV
                    label="Weekly Hours"
                    value={`${d.weekly_hours || 0} hrs ${d.meets_minimum ? '✓ Min met' : '⚠ Below 24hr'}`}
                    color={d.meets_minimum ? 'text-green-600' : 'text-red-600'}
                  />

                  <Sec title="Earnings" />
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <MiniStat label="All-Time Earnings" value={fmtMoney(d.all_time_earnings)} color="bg-green-50 text-green-700" />
                    <MiniStat label="Last 30 Days" value={fmtMoney(d.last_30_days)} color="bg-blue-50 text-blue-700" />
                    <MiniStat label="Pending Payout" value={fmtMoney(d.pendingPayout)} color="bg-amber-50 text-amber-700" />
                    <MiniStat label="Total Paid Out" value={fmtMoney(d.paidOut)} color="bg-gray-50 text-gray-700" />
                  </div>
                  <div className="mt-2">
                    <MiniStat label="Total Vehicles Verified" value={d.total_verified} color="bg-purple-50 text-purple-700" />
                  </div>

                  {d.assignments?.length > 0 && (
                    <>
                      <Sec title="Verification Assignments" />
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {d.assignments.map((a, i) => (
                          <div key={i} className="border rounded-xl p-2.5 text-xs">
                            <div className="flex justify-between mb-1">
                              <span className="font-mono text-blue-600">{a.verification_id || '—'}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                a.status === 'assigned' ? 'bg-blue-100 text-blue-700'
                                : a.status === 'completed' || a.status === 'submitted' ? 'bg-green-100 text-green-700'
                                : 'bg-yellow-100 text-yellow-700'
                              }`}>{a.status}</span>
                            </div>
                            <p className="text-gray-500">Seller: <strong>{a.seller_name || '—'}</strong></p>
                            {a.availability_slots?.[0] && (
                              <p className="text-gray-400">
                                {a.availability_slots[0].date} · {a.availability_slots[0].from_time}–{a.availability_slots[0].to_time}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.walletItems?.length > 0 && (
                    <>
                      <Sec title="Commission Wallet Entries" />
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {d.walletItems.slice(0, 20).map((item, i) => (
                          <div key={i} className="flex justify-between text-xs py-1.5 border-b border-gray-100">
                            <div>
                              <p className="text-gray-500">{fmtDate(item.created_at)} · {item.city}</p>
                              <p className="text-green-600 font-semibold">Partner: {fmtMoney(item.partner_amount)}</p>
                              <p className="text-gray-400 text-[10px]">Base: {fmtMoney(item.base_price)} · GST: {fmtMoney(item.gst_amount)}</p>
                            </div>
                            <span className={`self-center text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              item.payout_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {item.payout_status === 'paid' ? '✓ Paid' : 'Pending'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {d.avail?.length > 0 && (
                    <>
                      <Sec title="Availability Slots" />
                      <div className="grid grid-cols-2 gap-2">
                        {d.avail.map((a, i) => (
                          <div key={i} className="bg-gray-50 border rounded-lg p-2 text-xs text-center">
                            <p className="font-semibold">{a.date}</p>
                            <p className="text-gray-500">{a.from_time} – {a.to_time}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Individual record CSV */}
              <div className="mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    const row = {
                      Name: person.name,
                      Email: person.email || '',
                      Phone: person.phone || '',
                      City: person.city || '',
                      UID: person.user_uid || person.agent_uid || person.partner_uid || '',
                      Role: roleKey,
                      'Joined Date': fmtDate(person.created_at),
                      'Joined Time': fmtTime(person.created_at),
                      ...(roleKey !== 'partner' ? {
                        'Total Vehicles': d.total_vehicles || 0,
                        'Approved': d.approved || 0,
                        'Sold': d.sold || 0,
                        'Total Views': d.total_views || 0,
                      } : {}),
                      ...(roleKey === 'partner' ? {
                        'All Time Earnings': d.all_time_earnings || 0,
                        'Pending Payout': d.pendingPayout || 0,
                        'Total Verified': d.total_verified || 0,
                        'Payout Cycle': person.payout_cycle_days || 30,
                      } : {}),
                    };
                    downloadCSV([row], `bade_sahab_${roleKey}_${(person.name || 'record').replace(/\s+/g, '_')}.csv`);
                  }}
                  className="w-full flex items-center justify-center gap-2 border rounded-xl py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition"
                >
                  <Download className="h-4 w-4" />
                  Download This Record as CSV
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── FILTER PANEL ──────────────────────────────────────────────────────────────
const FilterPanel = ({ filters, setFilters, cities, roleKey, onClose }) => (
  <div className="border rounded-2xl p-4 bg-gray-50 mb-4">
    <div className="flex items-center justify-between mb-3">
      <p className="text-sm font-semibold text-gray-700">Filters & Sort</p>
      <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div>
        <Label className="text-xs">City</Label>
        <Select value={filters.city || 'all'} onValueChange={v => setFilters(f => ({ ...f, city: v === 'all' ? '' : v }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="All Cities" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {roleKey === 'partner' && (
        <>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Payout Cycle</Label>
            <Select value={filters.payout_cycle || 'all'} onValueChange={v => setFilters(f => ({ ...f, payout_cycle: v === 'all' ? '' : v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="15">15-day</SelectItem>
                <SelectItem value="30">30-day</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      )}

      {roleKey === 'agent' && (
        <div>
          <Label className="text-xs">Approval</Label>
          <Select value={filters.status || 'all'} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label className="text-xs">Sort By</Label>
        <Select value={filters.sort || 'newest'} onValueChange={v => setFilters(f => ({ ...f, sort: v }))}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest First</SelectItem>
            <SelectItem value="oldest">Oldest First</SelectItem>
            <SelectItem value="name_az">Name A–Z</SelectItem>
            <SelectItem value="name_za">Name Z–A</SelectItem>
            {(roleKey === 'seller' || roleKey === 'agent') && <SelectItem value="most_vehicles">Most Vehicles</SelectItem>}
            {roleKey === 'partner' && <SelectItem value="most_verified">Most Verified</SelectItem>}
          </SelectContent>
        </Select>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3 mt-3">
      <div>
        <Label className="text-xs">From Date</Label>
        <input type="date" value={filters.from_date || ''} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} className="w-full h-8 border rounded-lg px-2 text-xs" />
      </div>
      <div>
        <Label className="text-xs">To Date</Label>
        <input type="date" value={filters.to_date || ''} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} className="w-full h-8 border rounded-lg px-2 text-xs" />
      </div>
    </div>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const BadeSahabData = () => {
  const { token } = useAuth();
  const authH = { headers: { Authorization: `Bearer ${token}` } };

  const [activeRole, setActiveRole] = useState('user');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    city: '', status: '', sort: 'newest', from_date: '', to_date: '', payout_cycle: '',
  });

  const [allData, setAllData]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [cities, setCities]         = useState([]);
  const [page, setPage]             = useState(0);
  const sentinelRef = useRef(null);

  // Whether current tab is the edit logs tab (no table needed)
  const isEditLogsTab = activeRole === 'edit_logs';

  useEffect(() => {
    axios.get(`${API}/cities`).then(r => setCities(r.data.cities || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isEditLogsTab) return; // EditLogsPanel manages its own data
    setPage(0);
    setAllData([]);
    fetchAll();
  }, [activeRole]);

  const fetchAll = async () => {
    if (isEditLogsTab) return;
    setLoading(true);
    try {
      let results = [];

      if (activeRole === 'partner') {
        const res = await axios.get(`${API}/superadmin/partners`, authH);
        results = res.data.partners || [];

      } else if (activeRole === 'agent') {
        const res = await axios.get(`${API}/superadmin/agents`, authH);
        results = res.data.agents || [];

      } else {
        const res = await axios.get(`${API}/admin/vehicles?limit=1000`, authH);
        const vehicles = res.data.vehicles || [];

        const map = {};
        vehicles.forEach(v => {
          if (!v.seller_id) return;
          if (!map[v.seller_id]) {
            map[v.seller_id] = {
              id: v.seller_id,
              name: v.seller_name || 'Unknown',
              email: '—',
              phone: v.seller_phone || '—',
              city: v.city || '',
              role: v.seller_role || 'user',
              created_at: v.created_at,
              user_uid: v.seller_uid || null,
              interests: {},
              vehicle_count: 0,
              total_views: 0,
              wishlist_count: 0,
            };
          }
          map[v.seller_id].vehicle_count++;
          map[v.seller_id].total_views += (v.views_count || 0);
        });

        const allSellers = Object.values(map);
        results = activeRole === 'user'
          ? allSellers.filter(u => u.role === 'user' || u.role === 'seller')
          : allSellers;
      }

      setAllData(results);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    if (isEditLogsTab) return;
    setRefreshing(true);
    setPage(0);
    fetchAll();
  };

  // ── filtered + sorted list ───────────────────────────────────────────────
  const filtered = (() => {
    if (isEditLogsTab) return [];
    let data = [...allData];
    const q = search.toLowerCase();

    if (q) {
      data = data.filter(p =>
        [p.name, p.email, p.phone, p.city,
          p.user_uid, p.agent_uid, p.partner_uid,
          p.business_name, p.gst_number,
        ].some(v => v?.toLowerCase().includes(q))
      );
    }

    if (filters.city) data = data.filter(p => p.city === filters.city);

    if (activeRole === 'partner') {
      if (filters.status === 'active')   data = data.filter(p => p.active);
      if (filters.status === 'inactive') data = data.filter(p => !p.active);
      if (filters.payout_cycle) data = data.filter(p => String(p.payout_cycle_days || 30) === filters.payout_cycle);
    }
    if (activeRole === 'agent') {
      if (filters.status === 'approved') data = data.filter(p => p.agent_approved);
      if (filters.status === 'pending')  data = data.filter(p => !p.agent_approved);
    }

    if (filters.from_date) data = data.filter(p => (p.created_at || '') >= filters.from_date);
    if (filters.to_date)   data = data.filter(p => (p.created_at || '') <= filters.to_date + 'T23:59:59');

    const s = filters.sort;
    if      (s === 'oldest')        data.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
    else if (s === 'newest')        data.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    else if (s === 'name_az')       data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (s === 'name_za')       data.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    else if (s === 'most_vehicles') data.sort((a, b) => (b.vehicle_count || 0) - (a.vehicle_count || 0));
    else if (s === 'most_verified') data.sort((a, b) => (b.total_verified || 0) - (a.total_verified || 0));

    return data;
  })();

  const visible = filtered.slice(0, (page + 1) * PAGE_SIZE);
  const hasMore = visible.length < filtered.length;

  // infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || isEditLogsTab) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) setPage(p => p + 1);
    }, { threshold: 0.1 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, isEditLogsTab]);

  // reset page on filter change
  useEffect(() => { setPage(0); }, [search, filters]);

  const handleDownloadCSV = () => {
    if (isEditLogsTab) return;
    const rows = filtered.map(p => ({
      'Name': p.name || '',
      'Email': p.email || '',
      'Phone': p.phone || '',
      'City': p.city || '',
      'Role': p.role || activeRole,
      'UID': p.user_uid || p.agent_uid || p.partner_uid || '',
      'Joined Date': fmtDate(p.created_at),
      'Joined Time': fmtTime(p.created_at),
      'Status': p.active !== undefined
        ? (p.active ? 'Active' : 'Inactive')
        : (p.agent_approved !== undefined ? (p.agent_approved ? 'Approved' : 'Pending') : 'Active'),
      ...(activeRole === 'seller' || activeRole === 'user' ? {
        'Vehicle Count': p.vehicle_count || 0,
        'Total Views': p.total_views || 0,
      } : {}),
      ...(activeRole === 'partner' ? {
        'Partner UID': p.partner_uid || '',
        'Payout Cycle Days': p.payout_cycle_days || 30,
        'Active': p.active ? 'Yes' : 'No',
      } : {}),
      ...(activeRole === 'agent' ? {
        'Agent UID': p.agent_uid || '',
        'Agent Approved': p.agent_approved ? 'Yes' : 'No',
        'Vehicle Count': p.vehicle_count || 0,
        'Business Name': p.business_name || '',
        'GST': p.gst_number || '',
      } : {}),
    }));
    const dateLabel = filters.from_date ? `${filters.from_date}_to_${filters.to_date || 'today'}` : 'all';
    downloadCSV(rows, `bade_sahab_${activeRole}_${dateLabel}.csv`);
  };

  const uid = (p) => p.user_uid || p.agent_uid || p.partner_uid;
  const activeFiltersCount = [filters.city, filters.status, filters.payout_cycle, filters.from_date].filter(Boolean).length;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bade Sahab Data 👁️</h2>
          <p className="text-sm text-gray-500 mt-0.5">Master intelligence panel — complete data on all roles</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {!isEditLogsTab && (
            <>
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
                <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh'}
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadCSV} disabled={filtered.length === 0}>
                <Download className="h-4 w-4 mr-1.5" />
                Download CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Role tabs */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map(({ key, label, icon: Icon, activeClass }) => (
          <button
            key={key}
            onClick={() => {
              setActiveRole(key);
              setSearch('');
              setFilters({ city: '', status: '', sort: 'newest', from_date: '', to_date: '', payout_cycle: '' });
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
              activeRole === key ? activeClass : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── ADMIN EDIT LOGS TAB ─────────────────────────────────────── */}
      {isEditLogsTab ? (
        <AdminEditLogsPanel authH={authH} />
      ) : (
        <>
          {/* Search + filters — only for data tabs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${activeRole}s by name, phone, city, UID...`}
                className="pl-9"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="flex-shrink-0"
            >
              <Filter className="h-4 w-4 mr-1.5" />
              Filters {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
            </Button>
          </div>

          {showFilters && (
            <FilterPanel
              filters={filters}
              setFilters={setFilters}
              cities={cities}
              roleKey={activeRole}
              onClose={() => setShowFilters(false)}
            />
          )}

          {/* Count row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-500">
              Showing <strong>{visible.length}</strong> of <strong>{filtered.length}</strong> {activeRole}s
              {search && <> matching "<strong>{search}</strong>"</>}
              {loading && <Loader2 className="inline h-3 w-3 animate-spin ml-2 text-gray-400" />}
            </p>
            {(filters.from_date || filters.to_date) && (
              <p className="text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                {filters.from_date || '—'} → {filters.to_date || 'today'}
              </p>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto rounded-2xl border">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-3 text-gray-600 font-medium">Name &amp; UID</th>
                  <th className="text-left p-3 text-gray-600 font-medium">Contact</th>
                  <th className="text-left p-3 text-gray-600 font-medium">City</th>
                  {(activeRole === 'seller' || activeRole === 'user' || activeRole === 'agent') && (
                    <th className="text-left p-3 text-gray-600 font-medium">Vehicles</th>
                  )}
                  {activeRole === 'partner' && (
                    <th className="text-left p-3 text-gray-600 font-medium">Payout Cycle</th>
                  )}
                  <th className="text-left p-3 text-gray-600 font-medium">Status</th>
                  <th className="text-left p-3 text-gray-600 font-medium">Joined</th>
                  <th className="text-left p-3 text-gray-600 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((person, idx) => {
                  const id = uid(person);
                  return (
                    <tr key={person.id || idx} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                            activeRole === 'user'    ? 'bg-blue-100 text-blue-700'
                            : activeRole === 'seller'  ? 'bg-green-100 text-green-700'
                            : activeRole === 'agent'   ? 'bg-purple-100 text-purple-700'
                            : 'bg-amber-100 text-amber-700'
                          }`}>
                            {person.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-800 truncate">{person.name}</p>
                            {id && <p className="text-[10px] font-mono text-gray-400 truncate">{id}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-gray-600 truncate max-w-[140px]">{person.email !== '—' ? person.email : '—'}</p>
                        <p className="text-xs text-gray-500">{person.phone || '—'}</p>
                      </td>
                      <td className="p-3">
                        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {person.city || '—'}
                        </span>
                      </td>
                      {(activeRole === 'seller' || activeRole === 'user' || activeRole === 'agent') && (
                        <td className="p-3">
                          <p className="text-xs"><strong>{person.vehicle_count || 0}</strong> listed</p>
                          {(person.total_views || 0) > 0 && (
                            <p className="text-[10px] text-gray-400">
                              <Eye className="h-3 w-3 inline mr-0.5" />{person.total_views} views
                            </p>
                          )}
                        </td>
                      )}
                      {activeRole === 'partner' && (
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                            (person.payout_cycle_days || 30) === 15 ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
                          }`}>
                            {person.payout_cycle_days || 30}d
                          </span>
                        </td>
                      )}
                      <td className="p-3">
                        {activeRole === 'partner' ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${person.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {person.active ? 'Active' : 'Inactive'}
                          </span>
                        ) : activeRole === 'agent' ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${person.agent_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {person.agent_approved ? 'Approved' : 'Pending'}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                            {person.role || 'user'}
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <p className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(person.created_at)}</p>
                        <p className="text-[10px] text-gray-400">{fmtTime(person.created_at)}</p>
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs whitespace-nowrap"
                          onClick={() => setSelectedPerson(person)}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          Full Data
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!loading && filtered.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                <Search className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="font-medium">No {activeRole}s found</p>
                <p className="text-sm mt-1">Try adjusting the search or filters</p>
              </div>
            )}
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="py-2 text-center min-h-[24px]">
            {loading && <Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" />}
            {!hasMore && filtered.length > 0 && (
              <p className="text-xs text-gray-400">All {filtered.length} records loaded</p>
            )}
          </div>
        </>
      )}

      {/* Detail drawer — only for non-edit-logs tabs */}
      {selectedPerson && !isEditLogsTab && (
        <DetailDrawer
          person={selectedPerson}
          roleKey={activeRole}
          onClose={() => setSelectedPerson(null)}
          authH={authH}
        />
      )}
    </div>
  );
};

export default BadeSahabData;